/**
 * Turns a pasted definition-of-done document into checklist items.
 *
 * Real docs are markdown-ish rather than structured: numbered or bare headings,
 * `[ ]` checkboxes that some lines are missing, and an item written as
 * "Title: a paragraph explaining it". So the parser reports two lists — the
 * lines it is confident about, and the leftovers for a person to judge — rather
 * than guessing and quietly dropping half the document.
 */

const HEADING = /^(?:#{1,6}\s*)?(\d+)[.)]\s+(.{2,80})$/;
const CHECKBOX = /^[-*]?\s*\[\s*([xX])?\s*\]\s*(.+)$/;
// A preamble reads as prose; an item that lost its checkbox still reads as a
// "Title: explanation" line, and the title is short.
const TITLED = /^([^:]{3,60}):\s*(.+)$/;

const slug = (text) => text
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 48);

const splitTitle = (text) => {
  const match = TITLED.exec(text);
  if (!match) return { label: text.trim(), detail: '' };
  return { label: match[1].trim(), detail: match[2].trim() };
};

/**
 * @param {string} text the pasted document
 * @returns {{ items: Array, candidates: Array, standard: string }}
 *   items are the checkbox lines, candidates are lines that look like items but
 *   had no checkbox, and standard is a leading "Core standard"-style statement.
 */
export const parseDefinitionOfDone = (text) => {
  const lines = String(text || '').split(/\r?\n/);
  const items = [];
  const candidates = [];
  const used = new Set();
  let group = '';
  let standard = '';

  const push = (target, raw) => {
    const { label, detail } = splitTitle(raw);
    if (!label) return;

    // Two items can share a title across groups, so the id is qualified and then
    // made unique rather than colliding and merging their ticks.
    let id = [slug(group), slug(label)].filter(Boolean).join('--') || `item-${used.size + 1}`;
    let suffix = 2;
    while (used.has(id)) {
      id = `${id}-${suffix}`;
      suffix += 1;
    }
    used.add(id);
    target.push({ id, label, detail, group });
  };

  lines.forEach(line => {
    const trimmed = line.trim();
    if (!trimmed) return;

    const heading = HEADING.exec(trimmed);
    if (heading && !CHECKBOX.test(trimmed)) {
      group = heading[2].replace(/[:.]$/, '').trim();
      return;
    }

    const checkbox = CHECKBOX.exec(trimmed);
    if (checkbox) {
      push(items, checkbox[2]);
      return;
    }

    // The standard is the sentence before any group starts, and it belongs above
    // the checklist rather than inside it.
    if (!group && /standard|readiness/i.test(trimmed) && trimmed.includes(':')) {
      standard = trimmed.split(':').slice(1).join(':').trim();
      return;
    }

    if (TITLED.test(trimmed)) {
      push(candidates, trimmed);
    }
  });

  return { items, candidates, standard };
};

export default parseDefinitionOfDone;
